import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import FormWrapper from "@app/components/FormWrapper";
import AddBranchDeploymentForm from "@app/components/forms/AddBranchDeployment.form";
import { RoutePaths } from "@app/routes/index";
import { getBranchInitialData, insertVersion } from "@app/services/Endpoints/VersionControl.API";
import { BranchDeploymentErrors, ModalType } from "@app/types/Common.types";
import {
    AddBranchDeploymentFormData,
    AddBranchDeploymentPayload,
    AvailableVersions,
    BranchList,
} from "@app/types/VersionControl";
import { FormContext } from "@app/utils/contexts/Form.context";

const AddVersionManagementPage = () => {
    const navigate = useNavigate();
    const { triggerShowModal, setActions, isDirty, setIsDirty } = useContext(FormContext);
    const [branchList, setBranchList] = useState<BranchList[]>([]);
    const [availableVersions, setAvailableVersions] = useState<AvailableVersions[]>([]);
    const [errors, setErrors] = useState<BranchDeploymentErrors>({
        branchName: "",
        branchCode: "",
        availableVersion: "",
        expiryDate: "",
    });

    const [formData, setFormData] = useState<AddBranchDeploymentFormData>({
        branchName: "",
        branchCode: "",
        availableVersion: "",
        expiryDate: null,
        isActive: true,
    });

    const validateForm = () => {
        const currentErrors: BranchDeploymentErrors = {
            branchName: "",
            branchCode: "",
            availableVersion: "",
            expiryDate: "",
        };
        if (!formData.branchName) {
            currentErrors.branchName = t("ERRORS.REQUIRED", {
                name: t("VERSION_MANAGEMENT.ADD.NAME.LABEL"),
            });
        }
        if (!formData.branchCode) {
            currentErrors.branchCode = t("ERRORS.REQUIRED", {
                name: t("VERSION_MANAGEMENT.ADD.CODE.LABEL"),
            });
        }
        if (!formData.availableVersion) {
            currentErrors.availableVersion = t("ERRORS.REQUIRED", {
                name: t("VERSION_MANAGEMENT.ADD.AVAILABLE_VERSION.LABEL"),
            });
        }
        if (!formData.expiryDate) {
            currentErrors.expiryDate = t("ERRORS.REQUIRED", {
                name: t("VERSION_MANAGEMENT.ADD.EXPIRY_DATE.LABEL"),
            });
        }
        setErrors(currentErrors);
        return Object.values(currentErrors).every((e) => e === "");
    };

    const payload: AddBranchDeploymentPayload = {
        branchId: formData?.branchId,
        versionId: formData?.availableVersion,
        status: "Pending",
        expiryDate: formData?.expiryDate,
        isActive: formData?.isActive,
    };

    const insertBranchDeploymentData = (payload: AddBranchDeploymentPayload) => {
        insertVersion(payload)
            .then(() => {
                navigate(RoutePaths.ViewVersionManagement);
                setIsDirty(false);
            })
            .catch((err) => {
                console.error(err);
            });
    };

    const handleCancel = () => {
        if (isDirty) {
            triggerShowModal(true, ModalType.CANCEL);
            setActions({
                done: () => {
                    navigate(-1);
                    triggerShowModal(false, ModalType.CANCEL);
                    setIsDirty(false);
                },
                cancel: () => {
                    triggerShowModal(false, ModalType.CANCEL);
                },
            });
        } else {
            navigate(RoutePaths.ViewVersionManagement);
        }
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const res = await getBranchInitialData();
                const data = res?.data?.data;

                const branches = data?.branchList ?? [];
                setBranchList(
                    branches.map((b) => ({
                        branchName: b?.branchName,
                        branchCode: b?.branchCode,
                        branchId: b?.branchId,
                    })),
                );

                const versions = data?.nextAvailableVersions ?? [];
                setAvailableVersions(
                    versions.map((v) => ({
                        id: v?.id,
                        name: v?.name,
                    })),
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchInitialData();
    }, []);

    const handleSubmit = () => {
        if (!validateForm()) return;
        triggerShowModal(true, ModalType.SUBMIT);
        setActions({
            done: () => {
                insertBranchDeploymentData(payload);
                triggerShowModal(false, ModalType.SUBMIT);
            },
            cancel: () => triggerShowModal(false, ModalType.SUBMIT),
        });
    };

    return (
        <FormWrapper
            heading={t("VERSION_MANAGEMENT.ADD.ADD_HEADING.LABEL")}
            onCancelClick={handleCancel}
            onSubmitClick={handleSubmit}
            submitDisabled={!isDirty}
            noteSection={false}
        >
            <AddBranchDeploymentForm
                formData={formData}
                setFormData={setFormData}
                setIsDirty={setIsDirty}
                branchList={branchList}
                availableVersions={availableVersions}
                errors={errors}
                setErrors={setErrors}
            />
        </FormWrapper>
    );
};

export default AddVersionManagementPage;
